import { useEffect, useMemo, useState } from "react";
import { ArrowRight, MailCheck } from "lucide-react";

import { apiPost } from "../lib/api";
import { Card, LinkButton, StatusPill } from "./ui";

type VerifyState = "checking" | "success" | "error";

export function VerifyEmailPage() {
  const token = useMemo(() => new URLSearchParams(window.location.search).get("token") ?? "", []);
  const [state, setState] = useState<VerifyState>("checking");
  const [message, setMessage] = useState("Checking your verification link.");

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("This verification link is missing a token.");
      return;
    }

    let cancelled = false;
    void apiPost<{ verified: true }>("/auth/verify-email", { token })
      .then(() => {
        if (cancelled) return;
        setState("success");
        setMessage("Your iBox account email is verified. You can return to the desktop app.");
      })
      .catch((error) => {
        if (cancelled) return;
        setState("error");
        setMessage(error instanceof Error ? error.message : "This verification link could not be used.");
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <main className="relative min-h-screen overflow-hidden pt-16">
      <div className="download-bg absolute inset-0 z-0 bg-bg">
        <div className="absolute inset-0 bg-gt-grid opacity-45" />
        <div className="absolute inset-0 bg-bg/34" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-bg" />
      </div>

      <section className="relative z-10 mx-auto grid min-h-[calc(100vh-4rem)] max-w-4xl place-items-center px-6 py-24">
        <Card interactive={false} className="w-full p-8">
          <div className="flex items-start justify-between gap-6">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-bg text-primary">
              <MailCheck size={26} strokeWidth={1.6} />
            </div>
            <StatusPill
              status={state === "success" ? "success" : state === "error" ? "warning" : "info"}
              label={state === "success" ? "VERIFIED" : state === "error" ? "LINK FAILED" : "CHECKING"}
            />
          </div>

          <h1 className="mt-10 font-display text-5xl font-bold uppercase leading-none text-text">Verify Email</h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted">{message}</p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <LinkButton href="/download">
              Get iBox <ArrowRight size={14} />
            </LinkButton>
            <LinkButton href="/contact" variant="ghost">
              Contact Support
            </LinkButton>
          </div>
        </Card>
      </section>
    </main>
  );
}
